import React from 'react';
import { ProductParameterGroupTypes, ProductParameterTypes, ProductTaxonomyType } from '../../@sprinx/knihovka-types';
import {
  AfterPageProps,
  createApiClient,
  createInitialPropsRedirectToLogin,
  makeAfterComponent,
  wrapLayout,
} from '../../@sprinx/react-after-razzle';
import commonThingsToLoad from '../../api/commonThingsToLoad';
import {
  getProductDetail,
  productDetailInitialStateFamily,
  ProductDetailProduct,
} from '../../api/products/productDetail';
import {
  getProductParameterGroupTypes,
  productParameterGroupTypesInitialState,
} from '../../api/products/productParameterGroupTypes';
import { getProductParameterTypes, productParameterTypesInitialState } from '../../api/products/productParameterTypes';
import { getProductTaxonomyTypes, productTaxonomyTypesInitialState } from '../../api/products/taxonomyTypes';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import ErrorInternal from '../../components/ErrorInternal';
import CartAsideLayout from '../../layouts/CartAsideLayout';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';
import ProductDetailPageLayout from './ProductDetailPageLayout';
import ProductDetailPageLayoutSkeleton from './ProductDetailPageLayoutSkeleton';

export type ProductDetailPageProps = AfterPageProps;

const ProductDetailPage: React.FC<ProductDetailPageProps> = ({ error }) => {
  const t = useTranslateWithFallback();

  if (error) return <ErrorInternal error={error} />;

  return (
    <ErrorBoundary fallback={<div>{t('productDetailPage/notAvailable')}</div>}>
      <React.Suspense fallback={<ProductDetailPageLayoutSkeleton />}>
        <ProductDetailPageLayout />
      </React.Suspense>
    </ErrorBoundary>
  );
};

ProductDetailPage.displayName = 'ProductDetailPage';

export default makeAfterComponent(
  wrapLayout(ProductDetailPage, CartAsideLayout),
  async ({ match, initialAppProps: appProps }) => {
    const productId = match.params.id;
    try {
      if (appProps) {
        const apiClient = createApiClient({ accessToken: appProps.auth?.accessToken });

        const [taxonomyTypes, productData, productParameterTypes, productParameterGroupTypes, ...commonThings] =
          (await Promise.all([
            getProductTaxonomyTypes(apiClient),
            getProductDetail(
              apiClient,
              appProps.currency,
              appProps.currency === 'EUR' ? 4 : 2, // decimalPrecision,
              undefined, // enabledDynamicRules,
              appProps.pricesType,
              appProps.region,
              { id: productId },
            ),
            getProductParameterTypes(apiClient),
            getProductParameterGroupTypes(apiClient),
            ...commonThingsToLoad.promises({ apiClient }),
          ])) as [ProductTaxonomyType[], ProductDetailProduct, ProductParameterTypes, ProductParameterGroupTypes];

        appProps.propagateToState({
          ...commonThingsToLoad.toRegister(commonThings),
          [productTaxonomyTypesInitialState.key]: taxonomyTypes,
          [productDetailInitialStateFamily.key]: [productId, productData],
          [productParameterTypesInitialState.key]: productParameterTypes,
          [productParameterGroupTypesInitialState.key]: productParameterGroupTypes,
        });
      }

      return {};
    } catch (error) {
      if (error.response?.status === 401) {
        return createInitialPropsRedirectToLogin({ match });
      }
      return { error };
    }
  },
);
